import { useState, useEffect } from "react";
import { Box, Container, Typography, Button, Grid, Card, CardContent, Avatar, IconButton, keyframes } from "@mui/material";
import { WhatsApp, Telegram } from "@mui/icons-material";

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleUp = keyframes`
  from { transform: scale(1); }
  to { transform: scale(1.05); }
`;

// Contact Information
const WHATSAPP_NUMBER = "+923455861630";
const TELEGRAM_LINK = "https://t.me/premium_provider7";

const handleContact = (platform) => {
  if (platform === "whatsapp") {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hello, I am interested in buying a premium package.")}`, "_blank");
  } else if (platform === "telegram") {
    window.open(TELEGRAM_LINK, "_blank");
  } 
};

const BuyPremium = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const packages = [
    {
      title: "🌟 Basic Package",
      price: "Rs 100 / 7 Days",
      color: "#a0a0a0",
      gradient: "linear-gradient(45deg, #a0a0a0, #d3d3d3)",
      features: [
        "✅ Full Content Access",
        "✅ Mega Resources Collection",
        "🎓 Courses & Study Materials",
        "📚 Books & Audiobooks",
        "🕹️ Software & Games",
        "🎬 Movies & TV Series",
        "🇬🇧 English Media Collection",
        "🎌 Anime & Manga",
        "🕌 Islamic Collection",
        "🔑 1000+ Courses",
        "🦸‍♂️ Marvel Collection",
        "❌ No Custom Requests",
        "🖼️ HD Wallpapers"
      ]
    },
    {
      title: "✨ Mini Gold Package",
      price: "Rs 200 / 1 Month",
      color: "#ffcc00",
      gradient: "linear-gradient(45deg, #ffcc00, #ffdd55)",
      features: [
        "✅ Full Content Access",
        "✅ Mega Resources Collection",
        "🎓 Courses & Study Materials",
        "📚 Books & Audiobooks",
        "🕹️ Software & Games",
        "🇬🇧 English Media Collection",
        "🎌 Anime & Manga",
        "🕌 Islamic Collection",
        "🔑 1000+ Courses",
        "❌ No Custom Requests",
        "🖼️ HD Wallpapers"
      ]
    },
    {
      title: "🚀 Gold Package",
      price: "Rs 500 / 6 Months",
      color: "#ffd700",
      gradient: "linear-gradient(45deg, #ffd700, #ffec8b)",
      features: [
        "✅ Full Content Access",
        "✅ Mega Resources Collection",
        "🎓 Courses & Study Materials",
        "📚 Books & Audiobooks",
        "🕹️ Software & Games",
        "🇬🇧 English Media Collection",
        "🎌 Anime & Manga",
        "🕌 Islamic Collection",
        "🔑 1000+ Courses",
        "❌ Custom Requests",
        "🖼️ 4K Wallpapers",
      ]
      
    },
    {
      title: "🔥 Platinum Package",
      price: "Rs 1000 / 1 Year",
      color: "#e5e4e2",
      gradient: "linear-gradient(45deg, #e5e4e2, #f4f4f4)",
      features: [
        "✅ All Gold Features",
        "✅ Full Content Access",
        "✅ Mega Resources Collection",
        "🎓 Courses & Study Materials",
        "📚 Books & Audiobooks",
        "🕹️ Software & Games",
        "🇬🇧 English Media Collection",
        "🎌 Anime & Manga",
        "🕌 Islamic Collection",
        "🎯 Custom Requests",
        "🖼️ 4K Wallpapers",
        "🔑 1000+ Courses",
        "🔥 Early Access",
        "💬 VIP Support",


      ]
    },
    {
      title: "💎 Diamond Package",
      price: "Rs 2000 / Lifetime",
      color: "#b9f2ff",
      gradient: "linear-gradient(45deg, #b9f2ff, #e0f7fa)",
      features: [
        "✅ All Gold & Platinum Features",
        "✅ Mega Resources Collection",
        "✅ Full Content Access",
        "🌟 Lifetime Access",
        "🎓 Courses & Study Materials",
        "📚 Books & Audiobooks",
        "🕹️ Software & Games",
        "🇬🇧 English Media Collection",
        "🎌 Anime & Manga",
        "🕌 Islamic Collection",
        "🖼️ 4K Wallpapers",
        "⚡ Priority Support",
        "🎯 Custom Requests",
        "🔑 1000+ Courses",
        "💬 VIP Support",
        "🎁 Exclusive Gifts", 
        


      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{
      padding: { xs: 4, md: 8 },
      background: 'linear-gradient(135deg, #f5f7fa, #c3cfe2)',
      minHeight: '100vh',
      fontFamily: '"Montserrat", sans-serif',
    }}>
      {/* Profile Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        mb: 8,
        animation: `${fadeIn} 0.8s ease-out`
      }}>
        <Avatar 
          src="https://i.ibb.co/FkNLM99G/artworks-p-T0n-Uw9-Xbcw-Hc-Urm-mhdo-IA-t1080x1080.jpg" 
          sx={{ 
            width: 200, 
            height: 200, 
            mb: 4,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'rotate(360deg) scale(1.1)'
            }
          }} 
        />
        <Typography variant="h3" sx={{
          fontWeight: 700,
          color: '#2c3e50',
          mb: 2,
          textTransform: 'uppercase',
          letterSpacing: '2px'
        }}>
          Premium Packages
        </Typography>

        {/* Contact Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 3,
          '& .MuiIconButton-root': {
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.2)'
            }
          }
        }}>
          <IconButton 
            onClick={() => handleContact("whatsapp")}
            sx={{
              color: '#25D366',
              backgroundColor: 'rgba(37, 211, 102, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(37, 211, 102, 0.2)'
              }
            }}
          >
            <WhatsApp fontSize="large" />
          </IconButton>
          <IconButton 
            onClick={() => handleContact("telegram")}
            sx={{
              color: '#0088cc',
              backgroundColor: 'rgba(0, 136, 204, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0, 136, 204, 0.2)'
              }
            }}
          >
            <Telegram fontSize="large" />
          </IconButton>
        </Box>
      </Box>

      {/* Packages Grid */}
      <Grid container spacing={4} justifyContent="center">
        {packages.map((pkg, index) => (
          <Grid item xs={12} md={4} key={index}
            sx={{
              animation: `${fadeIn} 0.8s ease-out ${index * 0.2}s`,
              animationFillMode: 'backwards'
            }}
          >
            <Card sx={{
              borderRadius: 4,
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-10px)',
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2)',
                animation: `${scaleUp} 0.3s ease-out forwards`
              }
            }}>
              <CardContent sx={{
                background: pkg.gradient,
                textAlign: 'center',
                pb: '0 !important',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2))',
                  transform: 'rotate(45deg)',
                  transition: 'all 0.5s ease'
                },
                '&:hover::before': {
                  top: '50%',
                  left: '50%'
                }
              }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  color: '#2c3e50',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {pkg.title}
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#2c3e50',
                  my: 2,
                  position: 'relative',
                  zIndex: 1
                }}>
                  {pkg.price}
                </Typography>
              </CardContent>
              <CardContent>
                <Box component="ul" sx={{
                  listStyle: 'none',
                  pl: 0,
                  mb: 3,
                  '& li': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    fontSize: '0.95rem',
                    color: '#2c3e50',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(10px)'
                    }
                  }
                }}>
                  {pkg.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: pkg.color,
                    color: '#2c3e50',
                    fontWeight: 700,
                    py: 1.5,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      bgcolor: pkg.color
                    }
                  }}
                  onClick={() => handleContact("whatsapp")}
                >
                  Get {pkg.title.split(' ')[1]} Now
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default BuyPremium;
